//
//  AreaNetwork.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { SDKProject, } from "../../model/projects/SDKProject";
import { SDKProjectContext, } from "../../model/projects/SDKProjectContext";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Projects Area
/**
 * Manages project-related operations within a workspace using untyped data storage.
 */
export class AreaProjects {
    constructor(dataCore) {
        /** Key under which all projects are stored */
        this.PROJECTS_KEY = "projects";
        /** Key under which all project contexts are stored */
        this.PROJECT_CONTEXTS_KEY = "projectContexts";
        this.dataCore = dataCore;
    }
    /**
     * Fetches all projects for a given workspace.
     * @param workspaceId - The ID of the workspace.
     * @returns A promise resolving to an array of SDKProject instances.
     */
    async getAllProjects(workspaceId) {
        const projectsData = await this.dataCore.readKeyValueStorageData(workspaceId, this.PROJECTS_KEY);
        if (!projectsData || !Array.isArray(projectsData)) {
            return [];
        }
        return projectsData.map((data) => new SDKProject(data));
    }
    /**
     * Creates a new project in the specified workspace.
     * @param workspaceId - The ID of the workspace.
     * @param name - The name of the new project.
     * @returns A promise resolving to the created SDKProject instance.
     */
    async createProject(workspaceId, name, contextId) {
        const projects = await this.getAllProjects(workspaceId);
        const newProject = {
            id: uuidv4(),
            name,
            createdAt: new Date().toISOString(),
            contextId,
        };
        const updatedProjects = [...projects.map((p) => p.toRemote()), newProject];
        await this.dataCore.setKeyValueStorageData(workspaceId, this.PROJECTS_KEY, updatedProjects);
        return new SDKProject(newProject);
    }
    /**
     * Updates an existing project in the specified workspace.
     * @param workspaceId - The ID of the workspace.
     * @param updatedProject - The updated SDKProject instance.
     * @returns A promise that resolves when the update is complete.
     * @throws Error if the project is not found.
     */
    async updateProject(workspaceId, updatedProject) {
        const projects = await this.getAllProjects(workspaceId);
        const projectIndex = projects.findIndex((p) => p.id === updatedProject.id);
        if (projectIndex === -1) {
            throw new Error(`Project with id ${updatedProject.id} not found`);
        }
        const updatedProjects = projects.map((p) => p.id === updatedProject.id ? updatedProject.toRemote() : p.toRemote());
        await this.dataCore.setKeyValueStorageData(workspaceId, this.PROJECTS_KEY, updatedProjects);
    }
    /**
     * Deletes a project from the specified workspace.
     * @param workspaceId - The ID of the workspace.
     * @param projectId - The ID of the project to delete.
     * @returns A promise that resolves when the deletion is complete.
     */
    async deleteProject(workspaceId, projectId) {
        const projects = await this.getAllProjects(workspaceId);
        const updatedProjects = projects
            .filter((p) => p.id !== projectId)
            .map((p) => p.toRemote());
        await this.dataCore.setKeyValueStorageData(workspaceId, this.PROJECTS_KEY, updatedProjects);
    }
    /**
     * Retrieves all project contexts in a workspace.
     * @param workspaceId - The ID of the workspace.
     * @returns An array of SDKProjectContext instances.
     */
    async getAllProjectContexts(workspaceId) {
        const contextsData = await this.dataCore.readKeyValueStorageData(workspaceId, this.PROJECT_CONTEXTS_KEY);
        if (!contextsData || !Array.isArray(contextsData)) {
            return [];
        }
        return contextsData.map((data) => new SDKProjectContext(data));
    }
    /**
     * Creates a new project context.
     * @param workspaceId - The ID of the workspace.
     * @param creationModel - The properties for the new context.
     * @returns The created SDKProjectContext instance.
     */
    async createProjectContext(workspaceId, creationModel) {
        const contexts = await this.getAllProjectContexts(workspaceId);
        const newContextId = uuidv4();
        const newContextRemote = {
            id: newContextId,
            name: creationModel.name,
            description: creationModel.description,
            designSystemId: creationModel.designSystemId,
            dependencies: creationModel.dependencies,
            npmProxyRegistry: creationModel.npmProxyRegistry,
        };
        const updatedContexts = [
            ...contexts.map((c) => c.toRemote()),
            newContextRemote,
        ];
        await this.dataCore.setKeyValueStorageData(workspaceId, this.PROJECT_CONTEXTS_KEY, updatedContexts);
        return new SDKProjectContext(newContextRemote);
    }
    /**
     * Updates an existing project context.
     * @param workspaceId - The ID of the workspace.
     * @param updatedContext - The updated context instance.
     * @throws Error if the context is not found.
     */
    async updateProjectContext(workspaceId, updatedContext) {
        const contexts = await this.getAllProjectContexts(workspaceId);
        const contextIndex = contexts.findIndex((c) => c.id === updatedContext.id);
        if (contextIndex === -1) {
            throw new Error(`ProjectContext with id ${updatedContext.id} not found`);
        }
        const updatedContexts = contexts.map((c) => c.id === updatedContext.id ? updatedContext.toRemote() : c.toRemote());
        await this.dataCore.setKeyValueStorageData(workspaceId, this.PROJECT_CONTEXTS_KEY, updatedContexts);
    }
    /**
     * Deletes a project context.
     * @param workspaceId - The ID of the workspace.
     * @param contextId - The ID of the context to delete.
     */
    async deleteProjectContext(workspaceId, contextId) {
        const contexts = await this.getAllProjectContexts(workspaceId);
        const updatedContexts = contexts
            .filter((c) => c.id !== contextId)
            .map((c) => c.toRemote());
        await this.dataCore.setKeyValueStorageData(workspaceId, this.PROJECT_CONTEXTS_KEY, updatedContexts);
    }
}
